import { api } from './apiClient';

// ---------------------------------------------------------------------------
// Expense Management service — expenses + the expense-category master
// (migration 066). The apiClient auto-unwraps the { status, data } envelope,
// so these resolve to the inner payloads.
// ---------------------------------------------------------------------------

export interface ExpenseCategory {
  id: number;
  code: string;
  name: string;
  description: string | null;
  default_gl_account_id: number | null;
  default_gl_account_code?: string | null;
  default_gl_account_name?: string | null;
  /** Legacy display percentage. It no longer drives any calculation. */
  default_tax_rate: number | null;
  // Migration 089: the category's TAX CLASSIFICATION, which is what the tax
  // engine resolves a rate (and a treatment) from.
  default_tax_category_id?: number | null;
  default_sac_code?: string | null;
  is_reverse_charge?: number | boolean;
  receipt_required: number | boolean;
  spend_limit: number | null;
  is_active: number | boolean;
}

export interface ExpenseCategoryInput {
  code: string; name: string; description?: string;
  defaultGlAccountId?: number | null; defaultTaxRate?: number | null;
  defaultTaxCategoryId?: number | null; defaultSacCode?: string | null;
  isReverseCharge?: boolean;
  receiptRequired?: boolean; spendLimit?: number | null; isActive?: boolean;
}

export interface ExpenseRecord {
  id: number; expense_number: string; expense_date: string; posting_date?: string | null;
  employee_id: number | null; employee_name?: string | null;
  category: string | null; category_id: number | null; category_name?: string | null;
  department: string | null; department_id: number | null; department_name?: string | null; department_code?: string | null;
  description: string | null;
  amount: number; tax_amount: number; total_amount: number;
  cost_center_id: number | null; cost_center_name?: string | null;
  project_id: number | null; project_name?: string | null;
  gl_account_id: number | null; gl_account_code?: string | null; gl_account_name?: string | null; gl_account_code_resolved?: string | null;
  payment_method: string | null; paid_by: string;
  vendor_id: number | null; vendor_name?: string | null;
  status: string; journal_entry_id: number | null;
  approved_by_name?: string | null; approved_at?: string | null;
  paid_date?: string | null; payment_reference?: string | null; bank_account_id?: number | null;
  remarks: string | null; created_by_name?: string | null; created_at?: string;
}

export interface ExpenseInput {
  expenseDate: string; postingDate?: string; employeeId?: number;
  category?: string; categoryId?: number | null; description?: string;
  amount: number;
  /** Legacy: only honoured for genuinely tax-less entries. The server
   *  determines tax from taxCategoryId / the category when one resolves. */
  taxAmount?: number;
  taxCategoryId?: number | null;
  hsnSacCode?: string | null;
  placeOfSupply?: string | null;
  isReverseCharge?: boolean;
  isTaxInclusive?: boolean;
  costCenterId?: number | null; projectId?: number | null; departmentId?: number | null;
  glAccountId?: number | null; glAccountCode?: string;
  paymentMethod?: string; paidBy?: string; vendorId?: number | null; bankAccountId?: number | null;
  remarks?: string;
}

export const expenseService = {
  // --- expenses ---
  list: (filters?: Record<string, any>): Promise<{ expenses: ExpenseRecord[]; count: number }> =>
    api.get('/expenses', { params: filters }),
  summary: (): Promise<{ summary: any }> => api.get('/expenses/summary'),
  get: (id: number | string): Promise<{ expense: ExpenseRecord }> => api.get(`/expenses/${id}`),
  create: (payload: ExpenseInput): Promise<{ expense: ExpenseRecord }> => api.post('/expenses', payload),
  update: (id: number | string, payload: Partial<ExpenseInput>): Promise<{ expense: ExpenseRecord }> => api.put(`/expenses/${id}`, payload),
  submit: (id: number | string): Promise<{ expense: ExpenseRecord }> => api.post(`/expenses/${id}/submit`),
  approve: (id: number | string, comment?: string): Promise<{ expense: ExpenseRecord }> => api.post(`/expenses/${id}/approve`, { comment }),
  reject: (id: number | string, reason: string): Promise<{ expense: ExpenseRecord }> => api.post(`/expenses/${id}/reject`, { reason }),
  cancel: (id: number | string): Promise<{ expense: ExpenseRecord }> => api.post(`/expenses/${id}/cancel`),
  markPaid: (id: number | string, data?: { paymentDate?: string; paymentReference?: string; bankAccountId?: number }): Promise<{ expense: ExpenseRecord }> =>
    api.post(`/expenses/${id}/mark-paid`, data || {}),

  // --- category master ---
  listCategories: (activeOnly = false): Promise<{ categories: ExpenseCategory[]; count: number }> =>
    api.get('/expenses/categories', { params: activeOnly ? { activeOnly: 'true' } : {} }),
  createCategory: (payload: ExpenseCategoryInput): Promise<{ category: ExpenseCategory }> => api.post('/expenses/categories', payload),
  updateCategory: (id: number, payload: Partial<ExpenseCategoryInput>): Promise<{ category: ExpenseCategory }> => api.put(`/expenses/categories/${id}`, payload),
};

export default expenseService;
